import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ministeriosAPI, personasAPI } from "@/lib/api";
import { Plus, Edit, Trash2, HandHeart, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function MinisteriosPage() {
  const [ministerios, setMinisterios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingMinisterio, setEditingMinisterio] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    activo: true,
  });

  useEffect(() => {
    loadMinisterios();
  }, []);

  const loadMinisterios = async () => {
    try {
      setLoading(true);
      const response = await ministeriosAPI.getAll();
      setMinisterios(response.data);
    } catch (error) {
      toast.error("Error al cargar los ministerios");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (ministerio = null) => {
    if (ministerio) {
      setEditingMinisterio(ministerio);
      setFormData({
        nombre: ministerio.nombre,
        descripcion: ministerio.descripcion || "",
        activo: ministerio.activo,
      });
    } else {
      setEditingMinisterio(null);
      setFormData({ nombre: "", descripcion: "", activo: true });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMinisterio(null);
    setFormData({ nombre: "", descripcion: "", activo: true });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingMinisterio) {
        await ministeriosAPI.update(editingMinisterio.id, formData);
        toast.success("Ministerio actualizado correctamente");
      } else {
        await ministeriosAPI.create(formData);
        toast.success("Ministerio creado correctamente");
      }
      handleCloseDialog();
      loadMinisterios();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el ministerio");
    }
  };

  const handleDelete = async () => {
    try {
      await ministeriosAPI.delete(deleteId);
      toast.success("Ministerio eliminado correctamente");
      loadMinisterios();
    } catch (error) {
      toast.error("Error al eliminar el ministerio");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="ministerios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
            <HandHeart className="w-8 h-8 text-purple-600" strokeWidth={1.5} />
            Ministerios Parroquiales
          </h1>
          <p className="text-stone-600 mt-1">Gestión de ministerios y grupos parroquiales</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-rose-950 hover:bg-rose-900 text-white"
          data-testid="nuevo-ministerio-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Ministerio
        </Button>
      </div>

      {/* Ministerios Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-stone-400">Cargando...</div>
        </div>
      ) : ministerios.length === 0 ? (
        <Card className="border-stone-200 bg-white">
          <CardContent className="p-8 text-center text-stone-500">
            <HandHeart className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No hay ministerios registrados</p>
            <Button
              onClick={() => handleOpenDialog()}
              variant="outline"
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear primer ministerio
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ministerios.map((ministerio) => (
            <Card
              key={ministerio.id}
              className={`border-stone-200 bg-white card-hover ${
                !ministerio.activo ? "opacity-60" : ""
              }`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg font-serif text-stone-800">
                    {ministerio.nombre}
                  </CardTitle>
                  <Badge
                    variant="outline"
                    className={`${
                      ministerio.activo
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-stone-100 text-stone-500 border-stone-200"
                    }`}
                  >
                    {ministerio.activo ? "Activo" : "Inactivo"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {ministerio.descripcion && (
                  <p className="text-sm text-stone-600 line-clamp-2">
                    {ministerio.descripcion}
                  </p>
                )}
                <div className="space-y-3 pt-2 border-t border-stone-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-stone-500">
                      <Users className="w-4 h-4" />
                      <span>{ministerio.miembros_ids?.length || 0} miembros</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleOpenDialog(ministerio)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteId(ministerio.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <Link to={`/ministerios/${ministerio.id}/miembros`} className="block">
                    <Button
                      variant="outline"
                      className="w-full h-9 text-sm border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800 hover:border-purple-300"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Gestionar Miembros
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {editingMinisterio ? "Editar Ministerio" : "Nuevo Ministerio"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre del Ministerio *</Label>
              <Input
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                required
                className="h-11"
                placeholder="Ej: Coro Parroquial"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                rows={3}
                className="resize-none"
                placeholder="Breve descripción del ministerio..."
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="activo">Ministerio Activo</Label>
              <Switch
                id="activo"
                checked={formData.activo}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, activo: checked })
                }
              />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-rose-950 hover:bg-rose-900 text-white">
                {editingMinisterio ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este ministerio?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El ministerio será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
